import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { GuidedJourneyProvider } from '@/components/guidedJourney/GuidedJourneyProvider.comp.tsx';
import { AppFooter } from '@/components/layout/footer/AppFooter.comp.tsx';
import { AppRoutes } from '@/components/layout/routes/AppRoutes.comp.tsx';
import { TopBar } from '@/components/layout/topBar/TopBar.comp.tsx';
import { ConditionalToastContainer } from '@/components/ui/ConditionalToastContainer.comp.tsx';
import { GlobalAiAssistantFab } from '@/components/ui/GlobalAiAssistantFab.comp.tsx';
import { QueryClientWrapper } from '@/config/QueryClientWrapper.comp.tsx';
import { ForceDarkThemeProvider, KnowMeThemeProvider } from '@/config/theme/ThemeMode.context.tsx';
import { DictionariesContextProvider } from '@/contexts/dictionaries/DictionariesContextProvider.util.tsx';
import { FeatureFlagsContextProvider } from '@/contexts/featureFlagsContext/FeatureFlagsContextProvider.tsx';
import { PageContextProvider } from '@/contexts/pageContext/PageContext.provider.tsx';
import { PermissionErrorModalProvider } from '@/contexts/permissionErrorModal/PermissionErrorModalProvider.util.tsx';
import { UserContextProvider } from '@/contexts/userContext/UserContextProvider.util.tsx';
import { ImpersonationBanner } from '@/views/adminPanel/components/impersonation/ImpersonationBanner.comp';

const PracticalExamShareView = lazy(
  () => import('@/views/practicalExamShare/PracticalExamShare.view.tsx'),
);

const nonce = document.querySelector("meta[property='csp-nonce']")?.getAttribute('content') ?? '';
const cache = createCache({
  key: 'css',
  nonce,
  prepend: true,
});

function App() {
  return (
    <CacheProvider value={cache}>
      <PageContextProvider>
        <KnowMeThemeProvider>
          <LocalizationProvider adapterLocale="pl" dateAdapter={AdapterDayjs}>
            <QueryClientWrapper>
              <BrowserRouter>
                <Routes>
                  <Route
                    element={
                      <Suspense fallback={null}>
                        <PracticalExamShareView />
                      </Suspense>
                    }
                    path="/share/practical-exam/:token"
                  />
                  <Route
                    element={
                      <PermissionErrorModalProvider>
                        <UserContextProvider>
                          <GuidedJourneyProvider>
                            <FeatureFlagsContextProvider>
                              <DictionariesContextProvider>
                                <Box
                                  sx={{
                                    backgroundColor: 'primary.black',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: '100vh',
                                    width: '100%',
                                  }}
                                >
                                  <ForceDarkThemeProvider>
                                    <TopBar />
                                  </ForceDarkThemeProvider>
                                  <AppRoutes />
                                  <AppFooter />
                                  <GlobalAiAssistantFab />
                                  <ImpersonationBanner />
                                </Box>
                                <ConditionalToastContainer />
                              </DictionariesContextProvider>
                            </FeatureFlagsContextProvider>
                          </GuidedJourneyProvider>
                        </UserContextProvider>
                      </PermissionErrorModalProvider>
                    }
                    path="*"
                  />
                </Routes>
              </BrowserRouter>
            </QueryClientWrapper>
          </LocalizationProvider>
        </KnowMeThemeProvider>
      </PageContextProvider>
    </CacheProvider>
  );
}

export default App;
